import { IOsiCorrectionWidgetViewModel } from '@shared/types/mobx/widgets/OsiCorrection';
import { inject, injectable } from 'inversify';
import { makeAutoObservable, reaction } from 'mobx';
import moment from 'moment';
import api from '@app/api';
import logger from 'js-logger';
import { notifications } from '@shared/utils/notifications';
import { type OsiService, OsiServiceIdentifier } from '@shared/types/mobx/services/osi';
import axios from 'axios';
import { MIME, downloadBase64 } from '@shared/utils/download';
import { getGetApiTransactionsFixesOsiIdQueryOptions } from '@/shared/api/orval/transactions/transactions';
import { queryClient } from '@/shared/api/reactQuery';

@injectable()
export class OsiCorrectionWidgetViewModel implements IOsiCorrectionWidgetViewModel {
  fixes: any = [];

  isLoading = false;

  constructor(@inject(OsiServiceIdentifier) private osiModule: OsiService) {
    makeAutoObservable(this);

    reaction(
      () => this.period,
      async () => {
        if (!this.period[0] || !this.period[1]) return;

        await this.loadFixes();
      },
      { fireImmediately: true }
    );
  }

  private _date: any = new Date();

  period: any = [moment(this._date).startOf('month').toDate(), moment(this._date).endOf('month').toDate()];

  get date() {
    return this._date;
  }

  set date(value: any) {
    this._date = value;
    this.period = [moment(value).startOf('month').toDate(), moment(value).endOf('month').toDate()];
  }

  get rows() {
    return this.fixes?.map((e: any, idx: number) => ({ id: idx, ...e })) ?? [];
  }

  downloadReport = async (): Promise<void> => {
    try {
      if (!this.osiModule.osiId) throw new Error('osiId is undefined');

      this.isLoading = true;
      const data = await api.getFixesReport(
        this.osiModule.osiId,
        moment.utc(this.period[0]).local().format('YYYY-MM-DD[T]HH:mm:ss[Z]'),
        moment.utc(this.period[1]).local().format('YYYY-MM-DD[T]HH:mm:ss[Z]')
      );
      const osiName = this.osiModule.osiInfo?.name.startsWith(this.osiModule.osiInfo?.unionTypeRu ?? '')
        ? this.osiModule.osiInfo?.name
        : `${this.osiModule.osiInfo?.unionTypeRu} ${this.osiModule.osiInfo?.name}`;
      // iOS/WebKit: data:-URL не скачивается по атрибуту download — переведено на blob: через downloadBase64
      downloadBase64(
        data as unknown as string,
        MIME.XLSX,
        `Реестр_корректировок_${osiName}_${this.period[0].toISOString().substring(0, 10)}_${this.period[1]
          .toISOString()
          .substring(0, 10)}.xlsx`
      );
    } catch (e: unknown) {
      logger.error(e);
      if (axios.isAxiosError(e)) {
        notifications.error(e.message ?? 'Ошибка при скачивании отчета');
      }
    } finally {
      this.isLoading = false;
    }
  };

  loadFixes = async (): Promise<void> => {
    try {
      if (!this.osiModule.osiId) throw new Error('osiId is undefined');
      this.isLoading = true;

      const queryOptions = getGetApiTransactionsFixesOsiIdQueryOptions(
        this.osiModule.osiId,
        {
          dateBegin: moment.utc(this.period[0]).local().format('YYYY-MM-DD[T]HH:mm:ss[Z]'),
          dateEnd: moment.utc(this.period[1]).local().format('YYYY-MM-DD[T]HH:mm:ss[Z]')
        },
        { query: { gcTime: 0, staleTime: 0 } }
      );

      const response = await queryClient.fetchQuery(queryOptions);

      if (response.code !== 0) {
        throw new Error(response.message ?? 'Ошибка при запросе корректировок');
      }

      const sortedResp = response.result?.sort((a, b) => {
        if (!a.dt || !b.dt) return 0;

        if (a?.dt < b?.dt) return -1;
        if (a?.dt > b?.dt) return 1;
        return 0;
      });
      this.fixes = sortedResp || [];
    } catch (e: unknown) {
      logger.error(e);
      if (axios.isAxiosError(e)) {
        notifications.error(e.message ?? 'Ошибка при запросе корректировок');
      }
    } finally {
      this.isLoading = false;
    }
  };
}
